import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/authslice';
import { endpoints } from '../../api/api';
import AdminHeader from './components/AdminHeader';

interface Ticket {
  _id: string;
  topic: string;
  message: string;
  status: 'open' | 'resolved';
  adminNote?: string;
  createdAt: string;
  user: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  };
}

const SupportDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'resolved'>('all');
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await axios.get(endpoints.getAllSupportTickets, { params });
      setTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch support tickets', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleResolve = async (ticketId: string) => {
    setResolving(true);
    try {
      const url = endpoints.resolveSupportTicket.replace(':id', ticketId);
      const res = await axios.put(url, { adminNote });
      setTickets((prev) => prev.map((t) => (t._id === ticketId ? res.data.ticket : t)));
      setSelected(res.data.ticket);
      setAdminNote('');
    } catch (err) {
      console.error('Failed to resolve ticket', err);
    } finally {
      setResolving(false);
    }
  };

  const handleReopen = async (ticketId: string) => {
    try {
      const url = endpoints.reopenSupportTicket.replace(':id', ticketId);
      const res = await axios.put(url);
      setTickets((prev) => prev.map((t) => (t._id === ticketId ? res.data.ticket : t)));
      setSelected(res.data.ticket);
    } catch (err) {
      console.error('Failed to reopen ticket', err);
    }
  };

  const userName = (ticket: Ticket) => {
    const name = [ticket.user?.firstName, ticket.user?.lastName].filter(Boolean).join(' ');
    return name || ticket.user?.email || ticket.user?.phoneNumber || 'Unknown';
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader
        onLogout={handleLogout}
        onBack={() => navigate('/admin')}
        title="Help & Support"
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filter */}
        <div className="flex items-center gap-2 mb-6">
          {(['all', 'open', 'resolved'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${
                statusFilter === f
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f}
            </button>
          ))}
          <span className="ml-auto text-sm text-gray-400">{tickets.length} ticket{tickets.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="flex gap-6">
          {/* Ticket list */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400 text-sm">Loading...</div>
            ) : tickets.length === 0 ? (
              <div className="flex items-center justify-center py-20 text-gray-400 text-sm">No tickets found.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase tracking-wide">
                    <th className="text-left px-5 py-3 font-medium">User</th>
                    <th className="text-left px-5 py-3 font-medium">Topic</th>
                    <th className="text-left px-5 py-3 font-medium">Date</th>
                    <th className="text-left px-5 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket._id}
                      onClick={() => { setSelected(ticket); setAdminNote(ticket.adminNote || ''); }}
                      className={`border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selected?._id === ticket._id ? 'bg-orange-50' : ''
                      }`}
                    >
                      <td className="px-5 py-3 font-medium text-gray-900">{userName(ticket)}</td>
                      <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{ticket.topic}</td>
                      <td className="px-5 py-3 text-gray-400 whitespace-nowrap">{formatDate(ticket.createdAt)}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          ticket.status === 'open'
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-green-50 text-green-600'
                        }`}>
                          {ticket.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="w-80 flex-shrink-0 bg-white rounded-2xl border border-gray-200 p-5 flex flex-col gap-4 self-start">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">From</p>
                <p className="text-gray-900 font-medium text-sm">{userName(selected)}</p>
                {selected.user?.email && <p className="text-gray-500 text-xs mt-0.5">{selected.user.email}</p>}
                {selected.user?.phoneNumber && <p className="text-gray-500 text-xs">{selected.user.phoneNumber}</p>}
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Topic</p>
                <p className="text-gray-700 text-sm">{selected.topic}</p>
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Message</p>
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Date</p>
                <p className="text-gray-500 text-xs">{formatDate(selected.createdAt)}</p>
              </div>

              {selected.status === 'open' ? (
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">Note (optional)</label>
                  <textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    rows={3}
                    placeholder="Add a note before resolving..."
                    className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent placeholder-gray-300"
                  />
                  <button
                    onClick={() => handleResolve(selected._id)}
                    disabled={resolving}
                    className="mt-2 w-full bg-gray-900 text-white text-sm font-medium py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {resolving ? 'Resolving...' : 'Mark as Resolved'}
                  </button>
                </div>
              ) : (
                <div>
                  {selected.adminNote && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Note</p>
                      <p className="text-gray-600 text-sm leading-relaxed">{selected.adminNote}</p>
                    </div>
                  )}
                  <button
                    onClick={() => handleReopen(selected._id)}
                    className="w-full bg-white border border-gray-200 text-gray-700 text-sm font-medium py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reopen Ticket
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportDashboard;
